import {
  Field,
  Input,
  InputNumber,
  RadioGroup,
  Spin,
} from '@/components/commons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  ImageService,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import { COGEVO_STAMP_MONTH_PATTERN } from './formSchemaFn';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Form, Image, Upload } from 'antd';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface IProps {
  isLoading: boolean;
  isEdit: boolean;
  /** 月別の登録済み枚数。入力した月が何枚目になるかの案内に使う */
  countByMonth?: Map<string, number>;
}

const isActiveOptions = [
  { label: '有効', value: true },
  { label: '無効', value: false },
];

const kindOptions = [
  { label: '月別イラスト', value: false },
  { label: '既定イラスト', value: true },
];

export const CogevoStampImageForm: React.FC<IProps> = ({
  isLoading,
  isEdit,
  countByMonth,
}) => {
  const { watch, setValue, setError, clearErrors } = useFormContext();
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const url = watch('url');
  const isDefault = watch('isDefault');
  const month = watch('month');

  // 新規登録時のみ、その月で何枚目になるかを案内する
  const nextOrderInMonth =
    !isEdit && month && COGEVO_STAMP_MONTH_PATTERN.test(month)
      ? (countByMonth?.get(month) ?? 0) + 1
      : null;

  const handleUpload = async (file: File) => {
    try {
      setIsUploading(true);
      const res = await ImageService.uploadFile(file);
      setValue('url', res?.data?.url ?? '', { shouldValidate: true });
    } catch (error) {
      notificationMessage({
        message: 'イラスト画像のアップロードに失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > FILE_SIZE_LIMIT) {
      setError('url', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });

      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('url', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });

      return Upload.LIST_IGNORE;
    }

    clearErrors('url');
    handleUpload(file);

    return true;
  };

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <Field label='種別' name='isDefault' className='w-full'>
          <RadioGroup options={kindOptions} disabled={isEdit} />
        </Field>

        {!isDefault && (
          <>
            <Field label='対象月' name='month' required={!isEdit}>
              <Input placeholder='2026-09' maxLength={7} disabled={isEdit} />
            </Field>
            {nextOrderInMonth !== null && (
              <div className='mt-[-16px] mb-4 text-sm text-gray-500'>
                この月では
                <span className='font-semibold text-black'>
                  {nextOrderInMonth}枚目
                </span>
                のイラストになります（{nextOrderInMonth}
                枚目のカードに使われます）。
              </div>
            )}
          </>
        )}

        <label className='title-calendar font-semibold'>
          イラスト画像<span className='text-red-500'> *</span>
        </label>
        <div className='flex items-baseline gap-2 pt-2 mb-4'>
          {!url ? (
            <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                beforeUpload={(file: File) => beforeUpload(file)}
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                showUploadList={false}
              >
                <Button
                  type='default'
                  loading={isUploading}
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          ) : (
            <div className='relative inline-block'>
              <Image
                height={160}
                src={url}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => setValue('url', '', { shouldValidate: true })}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            </div>
          )}
        </div>
        {/* url のエラー表示用（画像ウィジェットは Field の外にあるため） */}
        <Field name='url' className='mt-[-20px]'>
          <></>
        </Field>

        <Field label='並び順（同じ月の中での順番）' name='sortOrder' required>
          <InputNumber
            placeholder='並び順'
            min={0}
            className='w-[120px] bg-white'
            style={{ backgroundColor: '#fff' }}
          />
        </Field>

        <Field label='有効状態' name='isActive' className='w-full'>
          <RadioGroup options={isActiveOptions} />
        </Field>
      </Spin>
    </Form>
  );
};
